import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { FileText, Search, Download, Book, FileCode, Wrench, AlertCircle, Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import toast from 'react-hot-toast'

interface Document {
  id: string
  title: string
  filename: string
  file_size: number
  file_type: string
  category: string
  description: string
  version: string
  uploaded_by: string
  is_public: boolean
  downloads: number
  uploaded_at: string
}

export default function Docs() {
  const { isDark } = useTheme()
  const { user } = useAuthStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Verificar si el usuario puede ver documentos privados
  const canViewPrivate = ['admin', 'technician'].includes(user?.role?.toLowerCase() || '')

  const categories = [
    { id: 'all', label: 'Todos', icon: FileText },
    { id: 'manual', label: 'Manuales', icon: Book },
    { id: 'specification', label: 'Especificaciones', icon: FileCode },
    { id: 'guide', label: 'Guías', icon: Wrench },
    { id: 'procedure', label: 'Procedimientos', icon: AlertCircle }
  ]

  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token')

      const response = await fetch(`${API_BASE}/documents/public`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Error al cargar documentos')
      }

      const data = await response.json()
      console.log('📄 Documentos cargados:', data)
      
      // Filtrar según permisos del usuario
      let filteredDocs = data.documents || []
      if (!canViewPrivate) {
        filteredDocs = filteredDocs.filter((doc: Document) => doc.is_public)
      }
      
      setDocuments(filteredDocs)
    } catch (err: any) {
      console.error('❌ Error loading documents:', err)
      setError(err.message)
      toast.error('Error al cargar documentos')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token')

      const response = await fetch(`${API_BASE}/documents/${docId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Error al descargar documento')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success('Documento descargado')
    } catch (err: any) {
      console.error('❌ Error downloading:', err)
      toast.error('Error al descargar documento')
    }
  }

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = 
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.filename.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            Documentación Técnica
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Manuales, especificaciones y guías técnicas
            {canViewPrivate && (
              <span className="ml-2 px-2 py-1 bg-purple-600 text-white text-xs rounded">
                Acceso completo
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar documentación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          <div className="flex gap-2 overflow-x-auto">
            {categories.map(cat => {
              const Icon = cat.icon
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-blue-600 text-white'
                      : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              )
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className={`text-center py-12 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            <AlertCircle className="w-16 h-16 mx-auto mb-4" />
            <p className="text-lg">{error}</p>
            <button
              onClick={loadDocuments}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Reintentar
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocs.length > 0 ? (
              filteredDocs.map(doc => (
                <div
                  key={doc.id}
                  className={`border rounded-xl p-6 hover:shadow-lg transition-shadow ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="w-8 h-8 text-blue-500" />
                    <div className="flex gap-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {categories.find(c => c.id === doc.category)?.label || doc.category}
                      </span>
                      {!doc.is_public && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-orange-600 text-white">
                          Privado
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {doc.title}
                  </h3>

                  {doc.description && (
                    <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {doc.description}
                    </p>
                  )}

                  <div className={`text-xs space-y-1 mb-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    <p>Tamaño: {formatFileSize(doc.file_size)}</p>
                    <p>Descargas: {doc.downloads || 0}</p>
                    <p>Subido: {formatDate(doc.uploaded_at)}</p>
                    {doc.version && <p>Versión: {doc.version}</p>}
                  </div>

                  <button
                    onClick={() => handleDownload(doc.id, doc.filename)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <FileText className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
                <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  No se encontraron documentos
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}